import { useCallback, useState } from "react";
import { FieldValues, UseFormTrigger } from "react-hook-form";

import { FormSection } from "./types";

export type { FormSection };

/**
 * Navegação genérica de formulários em etapas (wizard).
 *
 * Mantém o passo atual e só deixa avançar quando os campos do passo passam na
 * validação (`trigger`), de modo que o usuário corrige um bloco por vez em vez
 * de encarar a tela inteira de erros. A lógica de negócio continua no hook do
 * formulário; aqui mora só a navegação.
 *
 * `initialFurthestStep` semeia o passo mais avançado já "alcançado". Na **criação**
 * fica 0 (o usuário desbloqueia os passos ao avançar). Na **edição** passe
 * `steps.length - 1`: o registro já é válido, então todos os passos nascem
 * clicáveis no stepper — o usuário pula direto para a seção (e para o Salvar) que
 * quer, sem caçar campo. O gate de avanço continua valendo se ele tornar um
 * passo inválido ao editar.
 */
export function useFormWizard<T extends FieldValues>(
  steps: FormSection<T>[],
  trigger: UseFormTrigger<T>,
  initialFurthestStep = 0,
) {
  const [currentStep, setCurrentStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(() =>
    Math.max(0, Math.min(initialFurthestStep, steps.length - 1)),
  );
  // +1 ao avançar, -1 ao voltar — orienta a transição direcional na casca.
  const [direction, setDirection] = useState<1 | -1>(1);

  // Move para `target` registrando a direção (para a animação) num único lugar.
  const moveTo = useCallback((target: number) => {
    setCurrentStep((prev) => {
      setDirection(target >= prev ? 1 : -1);
      return target;
    });
  }, []);

  // Se a lista de passos encolher (ex.: um passo condicional some), o índice em
  // estado pode passar a apontar para fora do array. Clampa na renderização para
  // os consumidores nunca lerem `steps[undefined]` (o estado se reconcilia no
  // próximo movimento).
  const safeStep = Math.min(currentStep, steps.length - 1);

  const isFirst = safeStep === 0;
  const isLast = safeStep === steps.length - 1;

  const validateStep = useCallback(
    async (index: number) => {
      const fields = steps[index]?.fields;
      if (!fields?.length) return true;
      return trigger(fields);
    },
    [steps, trigger],
  );

  const goToStep = useCallback(
    async (target: number) => {
      const clamped = Math.max(0, Math.min(target, steps.length - 1));

      // Voltar é sempre livre.
      if (clamped <= currentStep) {
        moveTo(clamped);
        return;
      }

      // Avançar valida cada passo intermediário; para no primeiro inválido.
      for (let i = currentStep; i < clamped; i++) {
        if (!(await validateStep(i))) {
          moveTo(i);
          return;
        }
      }

      moveTo(clamped);
      setFurthestStep((prev) => Math.max(prev, clamped));
    },
    [currentStep, steps.length, validateStep, moveTo],
  );

  const goNext = useCallback(async () => {
    if (!(await validateStep(currentStep))) return false;
    if (!isLast) {
      const next = currentStep + 1;
      moveTo(next);
      setFurthestStep((prev) => Math.max(prev, next));
    }
    return true;
  }, [currentStep, isLast, validateStep, moveTo]);

  /** Solta todos os passos de uma vez (ex.: a pessoa disse que quer preencher tudo): o stepper nasce todo clicável. */
  const liberarTodos = useCallback(() => setFurthestStep(Number.MAX_SAFE_INTEGER), []);

  const goBack = useCallback(() => {
    if (!isFirst) moveTo(currentStep - 1);
  }, [isFirst, currentStep, moveTo]);

  return {
    currentStep: safeStep,
    /** Passo mais avançado já alcançado — usado para liberar saltos no stepper. */
    furthestStep: Math.min(furthestStep, steps.length - 1),
    /** Direção do último movimento (+1 avançar, -1 voltar) — orienta a animação. */
    direction,
    totalSteps: steps.length,
    isFirst,
    isLast,
    goNext,
    goBack,
    goToStep,
    liberarTodos,
  };
}

export type UseFormWizardReturn = ReturnType<typeof useFormWizard>;
